/* casam-promos.js — native cart promo / gift-with-purchase engine (EasyGift retirement, Phase 2).
   Reads window.CASAM_PROMOS (projected from shop metafield custom.active_cart_promos).
   Auto-adds gift when a cart qualifies, auto-removes our gift when disqualified, locks gift qty.
   Shopify's native automatic discount (created server-side at activate) makes the gift free.
   Collective items (vendor in exclude_vendors) are invisible to the engine. External asset; no inline-HTML. */
(function () {
  "use strict";

  // ---- pure helpers (also exported for node tests) ----
  function nid(x) { x = String(x == null ? "" : x); var m = x.match(/(\d+)\s*$/); return m ? m[1] : x; }

  function isGiftLine(line, promoKey) {
    var p = line && line.properties;
    if (!p) return false;
    if (promoKey == null) return !!p._casam_gift;
    return String(p._casam_gift) === String(promoKey);
  }

  function isExcluded(line, exclusions) {
    if (!exclusions) return false;
    var vend = (exclusions.exclude_vendors || []);
    if (line.vendor && vend.indexOf(line.vendor) > -1) return true;
    return false;
  }

  // qualifying (non-gift, non-excluded) lines, optionally filtered to trigger.products
  function qualifyingLines(cart, promo) {
    var trig = promo.trigger || {};
    var prodSet = (trig.products || []).map(nid);
    var resolved = (trig.resolved_product_ids || []).map(nid); // projection may resolve collections/tags → ids
    var wantIds = prodSet.concat(resolved);
    return (cart.items || []).filter(function (l) {
      if (isGiftLine(l)) return false;
      if (isExcluded(l, promo.exclusions)) return false;
      if (wantIds.length) {
        return wantIds.indexOf(nid(l.variant_id)) > -1 || wantIds.indexOf(nid(l.product_id)) > -1;
      }
      return true; // no product filter → any real line counts
    });
  }

  function qualifies(cart, promo) {
    var trig = promo.trigger || {};
    var matchAll = (trig.match || "all") !== "any";
    var conds = [];
    var ql = qualifyingLines(cart, promo);
    var qty = ql.reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
    var subtotal = ql.reduce(function (s, l) { return s + (l.line_price != null ? l.line_price : (l.price || 0) * (l.quantity || 0)); }, 0);

    if (trig.products || trig.resolved_product_ids) conds.push(ql.length > 0);
    if (trig.min_qty) conds.push(qty >= trig.min_qty);
    if (trig.min_subtotal != null) conds.push(subtotal >= trig.min_subtotal);
    if (trig.max_subtotal != null) conds.push(subtotal <= trig.max_subtotal);
    if (trig.selling_plan) conds.push(ql.some(function (l) { return !!l.selling_plan_allocation; }));  // audit #23: scope the subscription check to the promo's qualifying lines, not the whole cart
    if (!conds.length) return false;
    return matchAll ? conds.every(Boolean) : conds.some(Boolean);
  }

  function giftQty(promo, cart) {
    var g = promo.gift || {};
    if (g.qty_mode === "per_multiple" && (promo.trigger || {}).min_qty) {
      var qty = qualifyingLines(cart, promo).reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
      return Math.max(1, Math.floor(qty / promo.trigger.min_qty) * (g.qty || 1));
    }
    return g.qty || 1;
  }

  // Decide the cart mutations needed to reconcile the cart to the active promos.
  function computeActions(cart, promos) {
    var adds = [], removes = [], updates = [];
    var activeKeys = {};
    (promos || []).forEach(function (promo) {
      if (!promo || !promo.key) return;
      activeKeys[promo.key] = true;
      var gift = promo.gift || {};
      if (gift.type !== "product") return; // free_shipping handled by Shopify discount, no line
      var existing = (cart.items || []).filter(function (l) { return isGiftLine(l, promo.key); });
      var q = qualifies(cart, promo);
      if (q) {
        var want = giftQty(promo, cart);
        if (!existing.length) {
          adds.push({ id: nid(gift.variant_id), quantity: want, promo_key: promo.key });
        } else if (existing[0].quantity !== want) {
          updates.push({ key: existing[0].key, quantity: want });
        }
        // any duplicate gift lines for this promo beyond the first → remove
        existing.slice(1).forEach(function (l) { removes.push({ key: l.key }); });
      } else if (existing.length) {
        existing.forEach(function (l) { removes.push({ key: l.key }); });
      }
    });
    // orphan cleanup: gift lines whose promo is no longer active → remove
    (cart.items || []).forEach(function (l) {
      var k = l.properties && l.properties._casam_gift;
      if (k && !activeKeys[k]) removes.push({ key: l.key });
    });
    return { adds: adds, removes: removes, updates: updates };
  }

  // ---- open-ended "claim your free item" nudge (cheapest_free promos) ----
  // Open-ended buy-N-get-1 is a pure native discount (Shopify frees the cheapest
  // eligible item), but "buy 2 get 1" is 3-for-2 — the free item is a THIRD item.
  // A shopper with exactly the buy qty hasn't claimed anything yet, so at the cart
  // drawer we tell them clearly and suggest a free-eligible item to add.
  function openEndedPromos(promos) {
    return (promos || []).filter(function (p) { return p && (p.gift || {}).type === "cheapest_free"; });
  }

  // DRM-527: does THIS shopper hold the code this promo pays out through?
  // A site-wide promo (no requires_code) is a native automatic discount — true for everyone.
  // A landing-page promo pays out ONLY via a URL-scoped code, so the nudge is honest
  // exactly when that code is on the cart. Gating on cart truth rather than page URL means
  // the message follows the shopper off the landing page (PDP, collection, search) and
  // never promises a free blend to someone who'd be charged for it.
  //
  // Deliberately IGNORES Shopify's `applicable` flag. Measured on the live store with
  // promo-eligible blends and the code held:
  //     2 blends (one_away) → {code:"SEASONING", applicable:false}, total_discount 0
  //     3 blends (claimed)  → {code:"SEASONING", applicable:true},  total_discount 1699
  // So `applicable` means "is the threshold met right now", NOT "does the shopper hold the
  // code": it is false across the entire one_away window this nudge exists for, and true
  // only once the free blend is already in the cart. Gating on it would show the message
  // exclusively to shoppers who no longer need it. Presence is the ownership signal;
  // whether the threshold is met is nudgeState's job, and an expired code is handled
  // upstream (promo leaves v_cart_promos_active → nothing projected → engine dormant).
  //
  // NB: the Ajax cart DOES evaluate discount codes — an earlier read of this claimed
  // otherwise, from a test whose third item (Veggie Gold) sits outside the promo's
  // Promo-Eligible (All) collection, so customerGets matched nothing and the cart showed
  // no discount. That was an ineligible-product artifact, not a Shopify limitation.
  function promoLiveForCart(cart, promo) {
    var code = promo && promo.requires_code;
    if (!code) return true;                        // site-wide → Shopify enforces for everyone
    var want = String(code).toLowerCase();
    return ((cart && cart.discount_codes) || []).some(function (d) {
      return d && d.code && String(d.code).toLowerCase() === want;
    });
  }

  function buyCount(cart, promo) {
    return qualifyingLines(cart, promo).reduce(function (s, l) { return s + (l.quantity || 0); }, 0);
  }

  // How many free-eligible items (from the promo's free-set) are already in the cart.
  function freeSetInCart(cart, promo) {
    var ids = ((promo.gift || {}).gets_resolved_variant_ids || []).map(String);
    if (!ids.length) return 0;
    return (cart.items || []).reduce(function (s, l) {
      return s + (ids.indexOf(String(nid(l.variant_id))) > -1 ? (l.quantity || 0) : 0);
    }, 0);
  }

  // Returns the nudge to show for one open-ended promo (or {state:"idle"}).
  function nudgeState(cart, promo) {
    var trig = promo.trigger || {}, gift = promo.gift || {};
    var minq = trig.min_qty || 1, gq = gift.qty || 1, group = minq + gq;
    var n = buyCount(cart, promo);
    var earned = Math.floor(n / group);
    var rem = n % group;
    var nextIsFree = rem >= minq;        // the next eligible item added would be the free one
    var need = group - rem;              // eligible items to add to complete the next free
    var state;
    if (n <= 0) state = "idle";
    else if (nextIsFree) state = (earned >= 1 ? "more_free" : "one_away");
    else if (earned >= 1) state = "claimed";
    else state = "progress";
    return { state: state, earned: earned, need: need, nextIsFree: nextIsFree,
             minq: minq, gq: gq, group: group, n: n };
  }

  function nudgeText(ns) {
    switch (ns.state) {
      case "one_away":  return "🎁 You're 1 away — add one more blend and it's FREE!";
      case "more_free": return "🎁 " + ns.earned + " free already — add one more for another FREE blend!";
      case "claimed":   return "🎉 Nice! Your cheapest blend is FREE (" + ns.earned + " applied).";
      case "progress":  return "🎁 Buy " + ns.minq + ", get 1 FREE — add " + ns.need + " more to claim a free blend.";
      default:          return "";
    }
  }

  // ---- node export for tests ----
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { nid: nid, isGiftLine: isGiftLine, qualifyingLines: qualifyingLines, qualifies: qualifies, giftQty: giftQty, computeActions: computeActions, openEndedPromos: openEndedPromos, buyCount: buyCount, freeSetInCart: freeSetInCart, nudgeState: nudgeState, nudgeText: nudgeText, promoLiveForCart: promoLiveForCart };
    return;
  }

  // ---- browser runtime ----
  var promos = window.CASAM_PROMOS;
  if (!Array.isArray(promos) || !promos.length) return; // nothing active → engine dormant

  var applying = false, t = null;

  function getCart() { return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) { return r.json(); }); }

  function addItem(a) {
    var body = { id: a.id, quantity: a.quantity, properties: { _casam_gift: a.promo_key } };
    return fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  }
  function changeLine(c) {
    return fetch("/cart/change.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: c.key, quantity: c.quantity }) });
  }

  function reconcile() {
    if (applying) return;
    getCart().then(function (cart) {
      var act = computeActions(cart, promos);
      if (!act.adds.length && !act.removes.length && !act.updates.length) return; // stable
      applying = true;
      var progressed = false;
      function track(r) { if (r && r.ok) progressed = true; return r; }
      var chain = Promise.resolve();
      act.removes.forEach(function (r) { chain = chain.then(function () { return changeLine({ key: r.key, quantity: 0 }).then(track); }); });
      act.updates.forEach(function (u) { chain = chain.then(function () { return changeLine(u).then(track); }); });
      act.adds.forEach(function (a) { chain = chain.then(function () { return addItem(a).then(track); }); });
      chain.then(function () {
        applying = false;
        // only re-fire when a mutation actually landed — an all-failed pass (e.g. gift
        // variant sold out → 422) must not re-trigger reconcile every 350ms forever
        if (progressed) document.dispatchEvent(new CustomEvent("cart:build"));
      }).catch(function () { applying = false; });
    }).catch(function () {});
  }

  function debounced() { if (applying) return; clearTimeout(t); t = setTimeout(reconcile, 350); }

  ["cart:updated", "cart:build", "cart:open"].forEach(function (ev) { document.addEventListener(ev, debounced); });
  if (document.readyState !== "loading") reconcile(); else document.addEventListener("DOMContentLoaded", reconcile);

  // ---- nudge runtime: drawer "claim your free item" (open-ended cheapest_free) ----
  var oePromos = openEndedPromos(promos);

  // Campaign nudge blackout: hide specific promos' cart nudges during a date window so a holiday
  // campaign can own the cart messaging (e.g. Father's Day gift-set offer suppresses the concurrent
  // summer Buy-2-Get-1 nudge). Engine stays generic — the window/keys live in cart-drawer.liquid
  // (window.CASAM_PROMOS_SUPPRESS). This ONLY hides the nudge; the underlying offer is unchanged
  // (cheapest_free is enforced by Shopify's automatic discount, not this engine).
  (function () {
    var sup = window.CASAM_PROMOS_SUPPRESS;
    if (!sup || !sup.win || !sup.win.length) return;
    function md(s) { var p = s.split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); }
    var now = new Date(), s = md(sup.win[0]), e = md(sup.win[1]); e.setHours(23, 59, 59, 999);
    if (now < s || now > e) return;                 // outside the blackout — auto-resumes
    var keys = sup.keys || [];
    oePromos = oePromos.filter(function (p) { return keys.indexOf(p.key) < 0; });
  })();

  function findScrollable() {
    return document.querySelector("#CartDrawer .drawer__scrollable")
        || document.querySelector("[data-cart-drawer] .drawer__scrollable")
        || document.querySelector(".drawer__scrollable");
  }
  function ensureSlot() {
    var host = findScrollable();
    if (!host) return null;
    var el = host.querySelector("[data-casam-promo]");
    if (!el) {
      el = document.createElement("div");
      el.setAttribute("data-casam-promo", "");
      el.className = "casam-promo";
      var anchor = host.querySelector("[data-products]");
      if (anchor) host.insertBefore(el, anchor); else host.insertBefore(el, host.firstChild);
    }
    return el;
  }
  // Hide an already-rendered nudge without creating one that never existed — a shopper
  // who removes the code (or the last eligible blend) must not keep a stale promise.
  function clearSlot() {
    var el = document.querySelector("[data-casam-promo]");
    if (el) { el.hidden = true; el.innerHTML = ""; }
  }
  function cartItemsPayload(cart) {
    return (cart.items || []).map(function (l) {
      return { product_id: nid(l.product_id), variant_id: nid(l.variant_id),
               title: l.product_title || l.title, quantity: l.quantity, price: l.price };
    });
  }
  function addFree(variantId, btn) {
    if (btn) { btn.disabled = true; btn.textContent = "Adding…"; }
    // /cart/add.js resolves with 422 when the gift variant is sold out or inventory-capped,
    // so a resolved response is not success — restore the button and say why, or the shopper
    // is left with a dead "Adding…" and a nudge still insisting they're one away.
    function failed(msg) {
      if (!btn) return;
      btn.disabled = false; btn.textContent = "Add it FREE";
      var card = btn.parentNode, slot = card && card.parentNode;
      if (!slot) return;
      var err = slot.querySelector("[data-casam-promo-err]");
      if (!err) {
        err = document.createElement("div");
        err.setAttribute("data-casam-promo-err", "");
        err.style.cssText = "font-size:12px;color:#7a1620;font-weight:600;margin:-4px 0 8px;";
        slot.insertBefore(err, card.nextSibling);
      }
      err.textContent = msg;
    }
    fetch("/cart/add.js", { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: 1 }) })
      .then(function (r) {
        if (!r.ok) {
          failed(r.status === 422 ? "That one just sold out — refresh for another pick."
                                  : "Couldn’t add it. Please try again.");
          return;
        }
        document.dispatchEvent(new CustomEvent("cart:build"));
        document.dispatchEvent(new CustomEvent("cart:updated"));
      })
      .catch(function () { failed("Couldn’t add it. Please try again."); });
  }
  function fetchGiftSuggestion(cart, promo) {
    var ids = (promo.gift || {}).gets_resolved_variant_ids || [];
    if (!ids.length || !(window.CASAM_CART && window.CASAM_CART.endpoint)) return Promise.resolve(null);
    return fetch(window.CASAM_CART.endpoint, { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: cartItemsPayload(cart), gift_candidate_ids: ids }) })
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.ok && d.upsell) ? d.upsell : null; })
      .catch(function () { return null; });
  }
  var nudgeSeq = 0;
  function renderNudge() {
    if (!oePromos.length) return;
    var seq = ++nudgeSeq;
    getCart().then(function (cart) {
      if (seq !== nudgeSeq) return; // a newer render superseded this one
      // First promo that is BOTH live for this cart AND has something to say. Liveness and
      // speakability are separate predicates: selecting on liveness alone lets a live-but-idle
      // promo (e.g. a site-wide one scoped to a product family this cart lacks) shadow a
      // claimable one behind it, and the shopper is never told they're a blend from free.
      var promo = null, ns = null;
      for (var i = 0; i < oePromos.length; i++) {
        if (!promoLiveForCart(cart, oePromos[i])) continue;
        var cand = nudgeState(cart, oePromos[i]);
        if (cand.state === "idle") continue;
        promo = oePromos[i]; ns = cand; break;
      }
      if (!promo) { clearSlot(); return; }   // nothing claimable → say nothing (don't mint a slot)
      var slot = ensureSlot();
      if (!slot) return;
      slot.hidden = false;
      slot.innerHTML = '<div class="casam-promo__msg" style="background:#7a1620;color:#fff;'
        + 'padding:10px 12px;border-radius:8px;font-weight:600;text-align:center;margin:8px 0;">'
        + nudgeText(ns) + '</div>';
      if (ns.state === "one_away" || ns.state === "more_free") {
        fetchGiftSuggestion(cart, promo).then(function (u) {
          if (!u || slot.hidden || seq !== nudgeSeq) return;
          // endpoint data (u.title / u.image) enters via DOM properties only — never innerHTML
          var card = document.createElement("div");
          card.className = "casam-promo__pick";
          card.style.cssText = "display:flex;align-items:center;gap:10px;border:1px solid #e3c7c7;"
            + "border-radius:8px;padding:8px;margin:8px 0;";
          var img = document.createElement("img");
          img.alt = "";
          img.style.cssText = "width:48px;height:48px;object-fit:cover;border-radius:6px;";
          // audit #25: only load the gift image from same-origin (root-relative)
          // or an allow-listed CDN host — a protocol-relative "//evil/x.jpg" from
          // a bad endpoint response must not become an arbitrary outbound request.
          var src = String(u.image || ""), okSrc = false;
          if (src.charAt(0) === "/" && src.charAt(1) !== "/") { okSrc = true; }
          else if (/^https?:\/\//i.test(src)) {
            try {
              var h = new URL(src).hostname.toLowerCase();
              okSrc = /(^|\.)shopify\.com$/.test(h) || /(^|\.)shopifycdn\.com$/.test(h)
                   || /(^|\.)casamspice\.com$/.test(h) || /(^|\.)casam\.me$/.test(h);
            } catch (e) { okSrc = false; }
          }
          if (okSrc) img.src = src;
          var body = document.createElement("div");
          body.style.cssText = "flex:1;min-width:0;";
          var name = document.createElement("div");
          name.style.cssText = "font-weight:600;font-size:13px;";
          name.textContent = u.title || "";
          var tag = document.createElement("div");
          tag.style.cssText = "font-size:12px;color:#7a1620;font-weight:700;";
          tag.textContent = "FREE with your order";
          body.appendChild(name); body.appendChild(tag);
          var b = document.createElement("button");
          b.type = "button";
          b.className = "casam-promo__add";
          b.style.cssText = "background:#7a1620;color:#fff;border:0;border-radius:6px;"
            + "padding:8px 10px;font-weight:700;cursor:pointer;";
          b.textContent = "Add it FREE";
          b.addEventListener("click", function () { addFree(u.variant_id, b); });
          card.appendChild(img); card.appendChild(body); card.appendChild(b);
          slot.appendChild(card);
        });
      }
    }).catch(function () {});
  }
  var nt = null;
  function nudgeDebounced() { clearTimeout(nt); nt = setTimeout(renderNudge, 250); }

  /* The nudge must NOT depend on the theme firing a specific event — the same root cause
     casam-cart.js documents for the free-ship bar, which this listener set had inherited
     verbatim. Verified in theme.js: a first add dispatches ONLY 'ajaxProduct:added' (2850);
     'cart:updated' fires solely from the quantity-change path (1865); opening the drawer
     dispatches 'drawerOpen'/'drawerOpen.CartDrawer' (2332-2333), never 'cart:open'. So a
     shopper who lands on the promo page and then adds two blends from a PDP hit none of
     our three events and saw no nudge — exactly the one_away moment this exists for. The
     reconcile() fallback can't rescue it either: computeActions returns early for
     cheapest_free, so it never dispatches cart:build for a B2G1.
     Watch the invariant instead: theme.js buildCart() ALWAYS repopulates [data-products]
     on the first add and every change. That's event-name-agnostic, so it can't regress
     when the theme's event timing shifts. We render into a SIBLING of [data-products]
     (ensureSlot inserts before it), so our own writes never retrigger the observer. */
  ["cart:updated", "cart:build", "cart:open", "ajaxProduct:added", "drawerOpen.CartDrawer"]
    .forEach(function (ev) { document.addEventListener(ev, nudgeDebounced); });

  var nudgeObserver = null;
  function observeCartProducts() {
    if (nudgeObserver) return true;
    var host = document.querySelector("[data-products]");
    if (!host) return false;
    nudgeObserver = new MutationObserver(nudgeDebounced);
    nudgeObserver.observe(host, { childList: true, subtree: true });
    return true;
  }
  function nudgeInit() {
    // drawer markup is static liquid, but retry briefly in case we evaluate ahead of it
    if (!observeCartProducts()) {
      var tries = 0, iv = setInterval(function () {
        if (observeCartProducts() || ++tries > 20) clearInterval(iv);
      }, 150);
    }
    renderNudge();
  }
  if (document.readyState !== "loading") nudgeInit(); else document.addEventListener("DOMContentLoaded", nudgeInit);
})();
